import {useMemo, useState, useEffect, useCallback} from 'react'
/** @jsxImportSource @emotion/react */
import {css} from '@emotion/react'

import {crudAPI} from '../../apis' 

import {useIsMounted} from '../../hooks'

import * as Yup from 'yup'
import {parsePhoneNumberFromString} from 'libphonenumber-js'

import {Button, TextField, IconButton} from '@mui/material'
import {DataGrid} from '@mui/x-data-grid'
import CloseIcon from '@mui/icons-material/Close'



const styles = {
  root: css`
    position: relative;
    padding: 40px;
    
    h3 {
      margin-top: 25px;
      font-weight: 500;
      font-size: 1.2rem;
    }
  `,
  header: css`
    display: flex;
    flex-direction: column;
    margin-bottom: 30px;
    align-items: flex-start;
    justify-content: flex-start;

    p {
      max-width: 500px;
    }
  `,
  caption: css`
    color: #636363;
    font-weight: 500;
    font-size: 0.9rem;
  `,
  metrics: css`
    display: flex;
    flex-wrap: wrap;
    gap: 15px;
    margin-top: 20px;

    & > div {
      width: 120px;
      height: 90px;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      justify-content: center;
      padding: 12px;
      border: 2px solid #F1F1F1;
      border-radius: 8px;
      background-color: #F6F6F6;
    }

    h4 {
      margin: 0;
      font-size: 1rem;
      font-weight: 600;
    }

    p {
      text-transform: uppercase;
      font-size: 0.8rem;
      font-weight: 500;
    }
  `,
  form: css`
    display: flex;
    flex-direction: column;
    gap: 15px;
    margin-bottom: 30px;
    align-items: flex-start;

    & > div {
      display: flex;
      gap: 20px;
      flex-wrap: wrap;  
    }
  `,
  field: css`
    min-width: 260px;
    max-width: 340px;
    flex: 1 1 260px;
  `,
  button: css`
    min-width: 100px;
    height: 42px;
    box-shadow: none;
    font-weight: 500;
    text-transform: uppercase;
    font-size: 0.8rem;
    margin-top: 10px;
    
    &:hover {
      box-shadow: none;
    }
  `,
  table: css`
    margin-top: 30px;
  `,
  close: css`
    position: absolute;
    right: 10px;
    top: 10px;
  `
}


const COLOR_BY_STATUS = {
  pending:   '#FFA500',
  confirmed: '#28a745',
  maybe:     '#17a2b8',
  declined:  '#dc3545'
}


const inviteValidationSchema = Yup.object({
  name: Yup
    .string()
    .trim()
    .required('Name is required'),

  email: Yup
    .string()
    .trim()
    .email('Invalid email address')
    .notRequired(),

  phone: Yup
    .string()
    .trim()
    .notRequired()
    .test('is-phone', 'Invalid phone number', value => {
      if (!value) return true
      const phone = parsePhoneNumberFromString(value, 'US')
      // you can omit the `'US'` or swap for your default country
      return phone ? phone.isValid() : false
    })
})
.test(
  'email-or-phone',
  'Please provide either a valid email or phone number',
  ({ email, phone }) => Boolean(email || phone)
)


const columns = [
  { field: 'name',    headerName: 'Name',    flex: 1, minWidth: 150 },
  { field: 'email',   headerName: 'Email',   flex: 1, minWidth: 150 },
  { field: 'phone',   headerName: 'Phone',   flex: 1, minWidth: 90 },
  { field: 'comment', headerName: 'Comment', flex: 1, minWidth: 200 },
  {
    field: 'status',
    headerName: 'Status',
    minWidth: 120,
    renderCell: params => {
      const color = COLOR_BY_STATUS[params.value] || '#000'

      return (
        <span
          style={{
            padding: '4px 12px',
            borderRadius: 3,
            backgroundColor: `${color}1A`, // light tint
            color,
            textTransform: 'uppercase',
            fontWeight: 500,
            fontSize: '0.7rem',
          }}
        >
          {params.value}
        </span>
      )
    }
  }
]



const RSVPInviteForm = ({eventID, close}) => {
  const isMounted = useIsMounted()

  const [rsvps, setRsvps] = useState([])
  const [isFetching, setIsFetching] = useState(false)

  // local form
  const [manual, setManual] = useState({ name:'', email:'', phone:'' })
  const [errors, setErrors] = useState({})


  useEffect(() => {
    const fetchRsvps = async () => {
      setIsFetching(true)
      try {
        const res = await crudAPI.index(`/performances/${eventID}/rsvps`)
        if (isMounted.current) {
          setRsvps(
            res.data.data.map(r => ({
              id:     r.id,
              name:   r.attributes.name,
              email:  r.attributes.email,
              phone:  r.attributes.phone,
              status: r.attributes.status
            }))
          )
        }
      } catch (err) {
        // you could surface the error here if you like
        console.error('Failed to load RSVPs', err)
      } finally {
        if (isMounted.current) {
          setIsFetching(false)
        }
      }
    }
    fetchRsvps()
  }, [eventID, isMounted])



  const counts = useMemo(() => {
    return rsvps.reduce((acc, {status}) => {
      acc[status] = (acc[status] || 0) + 1
      return acc
    }, {
      maybe: 0,
      pending: 0,
      confirmed: 0,
      declined: 0
    })
  }, [rsvps])


  // POST invite
  const sendInvite = useCallback(async (person) => {
    const payload = {
      name:          person.name,
      email:         person.email || undefined,
      phone:         person.phone || undefined,
      performance_id: eventID
    }
    const resp = await crudAPI.create(
      `/performances/${eventID}/rsvps/invite`,
      payload
    )
    const data = resp.data.data
    return {
      id:     data.id,
      name:   data.attributes.name,
      email:  data.attributes.email,
      phone:  data.attributes.phone,
      status: data.attributes.status
    }
  }, [eventID])


  const handleClick = async (e) => {
    e.preventDefault()
    try {
      await inviteValidationSchema.validate(manual, { abortEarly: false })
      setErrors({})
      const newRow = await sendInvite(manual)
      // append to the single rsvps array
      setRsvps(prev => [...prev, newRow])
      setManual({ name:'', email:'', phone:'' })
    } catch (err) {
      if (err.name === 'ValidationError') {
        const map = {}
        err.inner.forEach(e => { if (!map[e.path]) map[e.path] = e.message })
        setErrors(map)
      } else {
        console.error(err)
      }
    }
  }



  function handleClose() {
    close?.()
  }


  return (
    <div css={styles.root}>
      <IconButton css={styles.close} onClick={handleClose}>
        <CloseIcon />
      </IconButton>

      <div css={styles.header}>
        <h3>Send RSVPs</h3>
        <p css={styles.caption}>
          Add a name and an email or phone number, then tap Send. 
          We’ll notify the people you invite by email, text, and push notification.
        </p>
      </div>

      <form css={styles.form}>
        <div>
          <TextField
            css={styles.field}
            label='Name' 
            name='name' 
            variant='filled' 
            fullWidth
            value={manual.name} 
            onChange={e => setManual(m => ({...m, name: e.target.value}))}
            error={!!errors.name} 
            helperText={errors.name}
          />
          <TextField
            css={styles.field}
            label='Email' 
            name='email' 
            variant='filled'
            fullWidth
            value={manual.email} 
            onChange={e => setManual(m => ({ ...m, email:e.target.value}))}
            error={!!errors.email} 
            helperText={errors.email}
          />
          <TextField
            css={styles.field}
            label='Phone'
            name='phone'
            variant='filled'
            fullWidth
            value={manual.phone}
            onChange={e => setManual(m => ({ ...m, phone:e.target.value}))}
            error={!!errors.phone} 
            helperText={errors.phone}
          />
        </div>

        <Button css={styles.button} type='button' onClick={handleClick}>
          Send
        </Button>
      </form>


      <section>
        <h3>Responses</h3>

        <div css={styles.metrics}>
          <div css={styles.invitedMetric}>
            <p>Invited</p>
            <h4>{rsvps.length}</h4>
          </div>
          <div css={styles.pendingMetric}>
            <p>Pending</p>
            <h4>{counts.pending}</h4>
          </div>
          <div css={styles.goingMetric}>
            <p>Going</p>
            <h4>{counts.confirmed}</h4>
          </div>
          <div css={styles.maybeMetric}>
            <p>Maybe</p>
            <h4>{counts.maybe}</h4>
          </div>
          <div css={styles.notGoingdMetric}>
            <p>Not Going</p>
            <h4>{counts.declined}</h4>
          </div>
        </div>
      </section>


      <div css={styles.table}>
        <h3>Invited</h3>
        <DataGrid
          rows={rsvps}
          columns={columns}
          density='compact'
          showToolbar
          autoHeight={false}
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: {
              paginationModel: { 
                pageSize: 10, 
                page: 0
              }
            }
          }}
          loading={isFetching}
          sx={{
            marginTop: '25px',

            '& .MuiDataGrid-columnHeader': {
              backgroundColor: '#f5f5f5',
            },
            // style the header row
            '& .MuiDataGrid-columnHeaders': {
              color: '#333',
              fontWeight: '500',
              fontSize: '0.8rem',
              textTransform: 'uppercase'
            },
            // style all cells
            '& .MuiDataGrid-cell': {
              fontSize: '0.9rem',
            },
            // zebra-striping
            '& .MuiDataGrid-row:nth-of-type(odd)': {
              backgroundColor: 'rgba(237, 237, 237, 0.1)',
            },
            // hover state
            '& .MuiDataGrid-row:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            }
          }}
        />
      </div>
    </div>
  )
}


export default RSVPInviteForm





